import Flatpickr from 'flatpickr';
import moment from 'moment-timezone';
import InputMasker from "~/helpers/inputMasker";
import { DatetimeMode } from "../models/datetimeMode";
import {TimeFormat} from "~/models/timeFormat"


export default class DatetimeHelper {
  input: HTMLElement;
  config: any;
  fp: any;
  parseTimezone: string = 'UTC';
  formatTimezone: string = 'UTC';
  hasTime: boolean = true
  static parseTimezone = 'UTC';
  static parseFormat = 'YYYY-MM-DD\\\\THH:mm:ss.SSSZ';
  static timezone = 'UTC';
  // static localTimeOffset = new Date().getTimezoneOffset();

  constructor(input: HTMLElement, config: any) {
    this.input = input;
    this.config = config;
    if (this.config.parseTimezone) {
      this.parseTimezone = this.config.parseTimezone;
      DatetimeHelper.parseTimezone = this.config.parseTimezone;
    }
    this.hasTime = !!config.hasTime || !!config.enableTime

    if (this.config.formatTimezone) this.formatTimezone = this.config.formatTimezone;

    this.config.disableMobile = true;
    this.config.parseDate = DatetimeHelper.parseDateFactory(this.parseTimezone, 'MM/DD/YYYY\\\\hh:mm a', this.hasTime);
    this.config.formatDate = DatetimeHelper.formatDateFactory(this.formatTimezone);
  }

  static parseDate(dateString: string, timezone: string = this.parseTimezone, format: string = this.parseFormat, hasTime: boolean = true): Date {
    // don't use format argument - it is the flatpickr default
    // to use custom format, access it from higher scope
    const timezonedDate = moment.tz(dateString, [format, this.parseFormat, ...this.manualInputFormats, ...this.additionalFormats], timezone);
    if (hasTime){
      return new Date(
        timezonedDate.year(),
        timezonedDate.month(),
        timezonedDate.date(),
        timezonedDate.hour(),
        timezonedDate.minute(),
        timezonedDate.second(),
        timezonedDate.millisecond(),
      );
    }else{
      return new Date(
        timezonedDate.year(),
        timezonedDate.month(),
        timezonedDate.date(),
      );
    }
  }

  static parseDateFactory(timezone: string = this.parseTimezone, format: string = this.parseFormat, hasTime: boolean = true): (s) => Date {
    return (dateString: string): Date => {
      return this.parseDate(dateString, timezone, format, hasTime);
    };
  }

  static formatDate(date: Date, timezone: string = this.timezone, format: string = this.displayFormat(DatetimeMode.Datetime)): string {
    if (!timezone) timezone = this.timezone
    if (!format) format = this.displayFormat(DatetimeMode.Datetime)
    return moment.tz([
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ], timezone).format(format);
  }

  static inLocalTime(date: Date): Date {
    return moment(date, this.timezone).add(-date.getTimezoneOffset(), 'm').toDate();
  }

  static toTimeString(dateString: string | Date, {format, fallback}: {format?: string, fallback?: string} = {}): string {
    const date = this.formatDate(new Date(dateString), this.timezone, format)
    return dateOrFallback(date, fallback)
  }

  static toLocalTimeString(dateString: string, {format, fallback}: {format?: string, fallback?: string} = {}): string {
    const date = this.formatDate(DatetimeHelper.inLocalTime(DatetimeHelper.parseDate(dateString, this.timezone)), this.timezone, format);
    return dateOrFallback(date, fallback)
  }

  /**
   * Effectively same as DatetimeHelper#toLocalTimeString except timezone is required
   * @param dateString
   * @param format
   * @param fallback
   * @param timezone
   */
  static toZonedTimeString(dateString: string, {format, fallback, timezone}: {format?: string, fallback?: string, timezone: string} = {timezone: this.timezone}): string {
    const date = this.formatDate(DatetimeHelper.parseDate(dateString, timezone), timezone, format)
    if (date === 'Invalid date' && fallback) {
      return fallback
    } else {
      return date
    }
  }

  static formatDateFactory(timezone: string): (date: Date, format: string) => string {
    return (date: Date, format: string): string => {
      return this.formatDate(date, timezone, format);
    };
  }

  static displayFormat(mode: number): string {
    switch (mode) {
      case DatetimeMode.Date:
        return DatetimeHelper.formats.date
      case DatetimeMode.Time:
        return DatetimeHelper.formats.time
      case DatetimeMode.Datetime:
        return DatetimeHelper.formats.datetime
    }
  }

  static readonly formats = {
    // September 4, 1986
    date: 'MMMM D, YYYY',
    // Sep 4, 1986
    dateShort: 'MMM D, YYYY',
    // 8:30 pm
    time: 'h:mm a',
    // September 4, 1986 8:30 pm
    datetime: 'MMMM D, YYYY h:mm a',
    // Sep 4, 1986 8:30 pm
    datetimeShort: 'MMM D, YYYY h:mm a',
    // September 4, 1986 8:30:45.375 pm
    millisecond: 'MMMM D, YYYY h:mm:ss.SSS a',
    // Sep 4th, 1986 at 8:30 pm
    shortMonthOrdinalDatetime: 'MMM Do, YYYY [at] h:mm a',
  }

  static datePickerDisplayFormat(mode: number, timeFormat?: TimeFormat): string {
    switch (mode) {
      case DatetimeMode.Date:
        return 'MM/DD/YYYY';
      case DatetimeMode.Time:
        if (timeFormat === TimeFormat.TwentyFourHour) {
          return 'H:mm';
        } else {
          return 'h:mm a';
        }
      case DatetimeMode.Datetime:
        if (timeFormat === TimeFormat.TwentyFourHour) {
          return 'MM/DD/YYYY H:mm'
        } else {
          return 'MM/DD/YYYY h:mm a';
        }
    }
  }

  static get manualInputFormats(): string[] {
    return [
      'MM/DD/YYYY h:mm a', // Both Date and Time
      'MM/DD/YYYY', // Date Only
      'h:mm a' // Time Only
    ];
  }

  static get additionalFormats(): (string | moment.MomentBuiltinFormat)[] {
    return [
      // for datetime date pickers which are in ISO_8601
      moment.ISO_8601,
      // for formulas
      'YYYY-MM-DD'
    ];
  }

  static initTabBehavior(config) {
    // be able to tab from year input to days
    if (!config.noCalendar) {
      $('.numInput.cur-year').on('keydown', e => {
        const key = e.which || e.keyCode;
        if (key === 9) {
          e.preventDefault();
          $('.flatpickr-day:first-child').trigger('focus')
        }
      });
    }

    // be able to shift-tab back from days to year input, or tab from days to hour input if visible
    $('.flatpickr-innerContainer').on('keydown', e => {
      const isInDayContainer = e.target.classList.contains('flatpickr-day');
      const hourInputVisible = $('.flatpickr-calendar.showTimeInput').length > 0;
      if (isInDayContainer) {
        const key = e.which || e.keyCode;
        if (e.shiftKey && key === 9) {
          e.preventDefault();
          $('.numInput.cur-year').trigger('focus')
        } else if (config.enableTime && hourInputVisible && key === 9) {
          e.preventDefault();
          $('.numInput.flatpickr-hour').trigger('focus')
        } else if (!hourInputVisible && key === 9) {
          e.preventDefault();
          $('.numInput.cur-year').trigger('focus');
        }
      }
    });

    // be able to shift-tab back from hour input to days, or from AM/PM to year input
    if (config.enableTime) {
      $('.numInput.flatpickr-hour').on('keydown', e => {
        const key = e.which || e.keyCode;
        if (e.shiftKey && key === 9) {
          e.preventDefault();
          $('.flatpickr-day:first-child').trigger('focus')
        }
      });

      $('.flatpickr-am-pm').on('keydown', e => {
        const key = e.which || e.keyCode;
        if (key === 9) {
          e.preventDefault();
          $('.numInput.cur-year').trigger('focus')
        }
      })
    }
  }

  static init(el: HTMLElement): DatetimeHelper {
    const $el = $(el);
    const config = $el.data('config');
    $el.removeAttr('data-config');

    const helper = new DatetimeHelper(el, config);
    helper.init();
    return helper;
  }

  init(): void {
    const id = this.input.getAttribute('id');
    this.fp = Flatpickr(this.input, this.config);
    if (!this.config.noCalendar) {
      this.fp.altInput.addEventListener('blur', e => {
        const currentValue = e.target.value;
        if (this.fp.config.allowInput) this.fp.setDate(currentValue, true);
      })
    }
    this.input.removeAttribute('id');
    this.fp.altInput.setAttribute('id', id);
    this.fp.altInput.setAttribute('aria-required', this.input.getAttribute('aria-required'));
    this.fp.altInput.setAttribute('aria-describedby', this.input.getAttribute('aria-describedby'));
    if (!this.config.enableTime) {
      this.fp.altInput.setAttribute('data-input-mask', 'date');
      InputMasker.initializeInputMasker(this.fp.altInput);
    }
    if (!this.config.noCalendar && this.config.enableTime) {
      this.fp.altInput.setAttribute('data-input-mask', 'datetime');
      InputMasker.initializeInputMasker(this.fp.altInput);
    }
    if (this.config.noCalendar && this.config.enableTime) {
      this.fp.altInput.setAttribute('data-input-mask', 'time');
      InputMasker.initializeInputMasker(this.fp.altInput);
    }

    DatetimeHelper.initTabBehavior(this.config)
  }
}


//Using number values so formats can be changed out in the future
//Need unique values amongst all options because datetime properties can pull all values in
export const dateTimeFormatOptions = [
  {label: "Jan 19th, 2020 at 10:22 pm", value: 1, parseFormat: 'MMM Do, YYYY [at] h:mm a'},
  {label: "Mon, Jan 19, 2020 at 10:22 pm", value: 2, parseFormat: 'ddd, MMM D, YYYY [at] h:mm a'},
  {label: "01/19/2020 at 10:22 PM", value: 3, parseFormat: 'MM/DD/YYYY [at] h:mm A'},
  {label: "1/19/20 at 10:22 PM", value: 4, parseFormat: 'M/D/YY [at] h:mm A'},
  {label: "01-19-2020 at 10:22 PM", value: 5, parseFormat: 'MM-DD-YYYY [at] h:mm A'},
  {label: "2020-19-01 at 10:22 PM", value: 6, parseFormat: 'YYYY-DD-MM [at] h:mm A'},
  {label: "19-Jan-2020 at 10:22 PM", value: 7, parseFormat: 'DD-MMM-YYYY [at] h:mm A'}
];
export const dateFormatOptions = [
  {label: "Jan 19th, 2020", value: 8, parseFormat: 'MMM Do, YYYY'},
  {label: "Mon, Jan 19, 2020", value: 9, parseFormat: 'ddd, MMM D, YYYY'},
  {label: "01/19/2020", value: 10, parseFormat: 'MM/DD/YYYY'},
  {label: "1/19/20", value: 11, parseFormat: 'M/D/YY'},
  {label: "01-19-2020", value: 12, parseFormat: 'MM-DD-YYYY'},
  {label: "2020-19-01", value: 13, parseFormat: 'YYYY-DD-MM'},
  {label: "19-Jan-2020", value: 14, parseFormat: 'DD-MMM-YYYY'}
];

export const timeFormatOptions = [
  {label: "10:22 pm", value: 15, parseFormat: 'h:mm a'},
  {label: "10:22:00 PM", value: 16, parseFormat: 'h:mm:ss A'},
  {label: "22:22", value: 17, parseFormat: 'H:mm'},
  {label: "22:22:00", value: 18, parseFormat: 'H:mm:ss'},
];
export const allDateTimeFormatOptions =[
  ...dateTimeFormatOptions,
  ...dateFormatOptions,
  ...timeFormatOptions
]

function dateOrFallback(date: string, fallback: string): string {
  if (date === 'Invalid date' && typeof fallback === 'string') {
    return fallback
  } else {
    return date
  }
}